import React, { useMemo, useState } from "react";
import ReviewTrendChart, { ReviewTrendDataPoint } from "@/lib/organisms/review-trend-chart";
import ReviewPerformanceTable from "@/lib/organisms/review-performance-table";
import SearchableSelect from "@/lib/shadcn/components/searchable-select";
import { MultiSelect } from "@/lib/shadcn/components/multi-select";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { useActiveApartmentEntities } from "@/store/slices/apartments";
import { useGetHistoricalAirbnbReviewsQuery, useGetReviewPerformanceQuery } from "@/store/booking-management-api";
import { usePeriodDates } from "@/hooks/usePeriodDates";
import { PeriodName } from "@/models";
import type { GetReviewPerformanceResponse } from "@/models/responses";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/lib/shadcn/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import { isPreviousPeriodSameAsPreviousYear } from "@/utils/periods";
import { formatDateToISODate } from "@/utils/dates";
import { computeWeightedPortfolioTrend } from "@/pages/graphs/reviews-aggregation";

const CHART_COLORS = {
    averageRating: "#f4a959", // volt/orange
    cleanliness: "#1e40af", // blue-800
    accuracy: "#1e293b", // slate-800
    communication: "#64748b", // slate-500
    location: "#2c533a", // green
    checkIn: "#ef4444", // red
    value: "#cbd5e1", // slate-300
};

const METRIC_OPTIONS = [
    { label: "Overall Rating", value: "averageRating" },
    { label: "Cleanliness", value: "cleanliness" },
    { label: "Accuracy", value: "accuracy" },
    { label: "Communication", value: "communication" },
    { label: "Location", value: "location" },
    { label: "Check-in", value: "checkIn" },
    { label: "Value", value: "value" },
];

// One line per apartment per metric: beyond this many apartments the comparison
// chart stops being legible, so the page asks for a narrower filter instead.
const MAX_COMPARISON_APARTMENTS = 12;

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Format a Date as "dd MMM yyyy" from its LOCAL components (the period dates are
// built as local Dates by getPeriodStartAndEndDates), so the window title reads
// the same regardless of the runner timezone.
function formatDayMonthYear(date: Date): string {
    return `${String(date.getDate()).padStart(2, "0")} ${MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()}`;
}

// Guarantee the highlighted span's endpoints exist as category points on the
// chart's x-axis, so the Recharts ReferenceArea always resolves (and shades)
// even in a scrape gap where the trend series is empty (FR-020/FR-021).
function withHighlightAnchors(data: ReviewTrendDataPoint[], highlight: { from: string; to: string }): ReviewTrendDataPoint[] {
    const dates = new Set(data.map((d) => d.date));
    const extra: ReviewTrendDataPoint[] = [];
    if (!dates.has(highlight.from)) extra.push({ date: highlight.from });
    if (!dates.has(highlight.to)) extra.push({ date: highlight.to });
    if (extra.length === 0) return data;
    return [...data, ...extra].sort((a, b) => a.date.localeCompare(b.date));
}

// Traceability: airbnb-review-performance-table AS-10/AS-11/AS-12/FR-014. The
// page now reads Year + Period from the shared userSettings slice (the free-form
// date pickers were removed, SC-008); the chart shows a trailing 12-month window
// with the selected period shaded, and the period-bound performance table sits
// below it in every tab.
const ReviewsGraphsPage: React.FC = () => {
    const apartmentEntities = useActiveApartmentEntities();

    // Four apartment filters via the shared slice / `<ApartmentFilterBar />`.
    const { predicates } = useApartmentFilters();

    const [selectedMetrics, setSelectedMetrics] = useState<string[]>(["averageRating"]);

    // `selectedGroup` is read ONLY by the Group Trends tab, where the group is
    // the breakdown axis rather than a filter. Apartment scope for the rest of
    // the page comes exclusively from the shared filter bar.
    const { selectedGroup, selectedYear, selectedPeriod, groupOptions, periodOptions, yearOptions } = useAppData();
    const { setSelectedGroup, setSelectedYear, setSelectedPeriod } = useUserSettingsDispatch();

    // The selected period drives BOTH windows: the table is period-bound, the
    // chart shows the trailing 12 months ending at the period's end date.
    const { current: currentPeriod } = usePeriodDates(selectedYear, selectedPeriod);
    const periodStart = currentPeriod.startDate;
    const periodEnd = currentPeriod.endDate;
    const windowStart = new Date(periodEnd.getFullYear(), periodEnd.getMonth() - 12, periodEnd.getDate());

    // Fetch the 12-month trend for the chart.
    const { data: reviews } = useGetHistoricalAirbnbReviewsQuery({
        startDate: formatDateToISODate(windowStart),
        endDate: formatDateToISODate(periodEnd),
    });

    // Fetch the period-bound performance table (backend computes every measure).
    const {
        data: performance,
        isFetching: isPerformanceFetching,
        isError: isPerformanceError,
    } = useGetReviewPerformanceQuery({ year: selectedYear.toString(), period: selectedPeriod });

    const periodLabel = performance?.periodLabel ?? `${selectedPeriod} ${selectedYear}`;

    const chartTitle = `Review Trend · 12 months to ${formatDayMonthYear(periodEnd)}`;
    const highlight = useMemo(
        () => ({ from: formatDateToISODate(periodStart), to: formatDateToISODate(periodEnd), label: periodLabel }),
        [periodStart, periodEnd, periodLabel],
    );

    // The ONE apartment-scope set for this page: the shared `<ApartmentFilterBar />`
    // predicates (bedrooms / apartments / section / owner-group / group / tags /
    // show-decommissioned), and nothing else. It drives the chart, the comparison
    // series and the performance table alike, so the three can never disagree.
    //
    // Deliberately NOT ANDed with `userSettings.selectedApartment` /
    // `selectedGroup`: `userSettingsSlice` treats their `0` ("All") sentinel as
    // "unset" and overwrites it with the alphabetically-first apartment/group
    // whenever apartments load. A page that reads them cannot represent "All"
    // across a reload — it silently narrows to one arbitrary apartment, and to a
    // group that usually doesn't contain it, leaving the user with no data.
    const filteredApartmentIds = useMemo(() => {
        return Object.values(apartmentEntities)
            .filter((apartment) => predicates.every((predicate) => predicate(apartment)))
            .map((a) => a.id);
    }, [apartmentEntities, predicates]);

    const filteredRows = useMemo(() => {
        if (!performance) return [];
        const ids = new Set(filteredApartmentIds);
        return performance.rows.filter((row) => ids.has(row.apartmentId));
    }, [performance, filteredApartmentIds]);

    // The comparison chart draws one line per apartment per metric. Left
    // unbounded it would paint the whole portfolio (50+ apartments) the moment
    // the page loads unfiltered, which is unreadable and slow — so it asks the
    // user to narrow the shared bar instead of silently truncating the series.
    const comparisonIsReadable =
        filteredApartmentIds.length > 0 && filteredApartmentIds.length <= MAX_COMPARISON_APARTMENTS;

    // Transform reviews into aggregated trend data (filtered by current filters),
    // volume-weighted per date per metric (FR-022).
    const aggregatedData = useMemo(() => {
        if (!reviews || filteredApartmentIds.length === 0) return [];
        const filtered = reviews.filter((review) => filteredApartmentIds.includes(review.apartmentId));
        return computeWeightedPortfolioTrend(filtered);
    }, [reviews, filteredApartmentIds]);

    // Prepare data for individual apartments comparison. The series ARE the
    // filter-bar selection — no second apartment picker on the page.
    const apartmentComparisonData = useMemo(() => {
        if (!reviews || !comparisonIsReadable || selectedMetrics.length === 0) return [];

        const selectedApartmentIds = filteredApartmentIds;

        // Get reviews for selected apartments
        const selectedReviews = reviews.filter((review) => selectedApartmentIds.includes(review.apartmentId));

        // Get all unique dates
        const allDates = new Set<string>();
        selectedReviews.forEach((review) => {
            allDates.add(formatDateToISODate(review.date));
        });

        // Build combined data points with apartment-metric combinations
        return Array.from(allDates)
            .sort()
            .map((date) => {
                const dataPoint: ReviewTrendDataPoint = { date };
                selectedApartmentIds.forEach((aptId) => {
                    const apt = apartmentEntities[aptId];
                    const aptReview = selectedReviews.find((r) => r.apartmentId === aptId && formatDateToISODate(r.date) === date);
                    if (apt && aptReview) {
                        // Add a line for each apartment-metric combination
                        selectedMetrics.forEach((metricValue) => {
                            const key = `${apt.name} - ${METRIC_OPTIONS.find((m) => m.value === metricValue)?.label}`;
                            dataPoint[key] = aptReview[metricValue as keyof typeof aptReview] as number;
                        });
                    }
                });
                return dataPoint;
            });
    }, [reviews, comparisonIsReadable, filteredApartmentIds, apartmentEntities, selectedMetrics]);

    const apartmentComparisonLines = useMemo(() => {
        const colors = Object.values(CHART_COLORS);
        const lines: Array<{ dataKey: string; name: string; color: string }> = [];

        filteredApartmentIds.forEach((aptId, aptIndex) => {
            const apt = apartmentEntities[aptId];
            if (!apt) return;
            selectedMetrics.forEach((metricValue, metricIndex) => {
                const metric = METRIC_OPTIONS.find((m) => m.value === metricValue);
                const key = `${apt.name} - ${metric?.label}`;
                lines.push({
                    dataKey: key,
                    name: key,
                    color: colors[(aptIndex * selectedMetrics.length + metricIndex) % colors.length],
                });
            });
        });

        return lines;
    }, [filteredApartmentIds, apartmentEntities, selectedMetrics]);

    // Build chart lines based on selected metrics
    const selectedMetricLines = useMemo(() => {
        return selectedMetrics.map((metricValue) => {
            const metric = METRIC_OPTIONS.find((m) => m.value === metricValue);
            return {
                dataKey: metricValue,
                name: metric?.label || metricValue,
                color: CHART_COLORS[metricValue as keyof typeof CHART_COLORS],
                strokeWidth: metricValue === "averageRating" ? 3 : 2,
            };
        });
    }, [selectedMetrics]);

    // Prepare data for group trends
    const groupTrendData = useMemo(() => {
        if (!reviews || selectedGroup === 0) return [];

        // Get apartment IDs in the selected group
        const groupApartmentIds = Object.values(apartmentEntities)
            .filter((apt) => apt.groupId === selectedGroup)
            .map((apt) => apt.id);

        if (groupApartmentIds.length === 0) return [];

        // Get reviews for apartments in the group, volume-weighted per date (FR-022).
        const groupReviews = reviews.filter((review) => groupApartmentIds.includes(review.apartmentId));
        return computeWeightedPortfolioTrend(groupReviews);
    }, [reviews, selectedGroup, apartmentEntities]);

    // Every chart on the page shades the selected period with a Recharts
    // ReferenceArea, so every chart's data needs the highlight's endpoints
    // present as x-axis categories. Miss one and that chart's ReferenceArea
    // resolves to NaN x/width — Recharts warns and the shading silently
    // disappears.
    const aggregatedChartData = useMemo(() => withHighlightAnchors(aggregatedData, highlight), [aggregatedData, highlight]);
    const apartmentComparisonChartData = useMemo(
        () => withHighlightAnchors(apartmentComparisonData, highlight),
        [apartmentComparisonData, highlight],
    );
    const groupTrendChartData = useMemo(() => withHighlightAnchors(groupTrendData, highlight), [groupTrendData, highlight]);

    // The period-bound performance table + its page-level states (the table owns
    // its own loading skeleton + all-no-data alert; the page owns the two empty
    // variants). Rendered below the chart in every tab.
    const performanceSection = (() => {
        if (!performance) {
            // Query still in flight — render the table in its loading state so the
            // skeleton shows and the measure toggle is disabled.
            const loadingData: GetReviewPerformanceResponse = {
                periodLabel,
                showPreviousPeriod: !isPreviousPeriodSameAsPreviousYear(selectedPeriod),
                showPreviousYear: true,
                rows: [],
            };
            return <ReviewPerformanceTable data={loadingData} loading />;
        }
        if (performance.rows.length === 0) {
            return (
                <Card>
                    <CardContent className="flex items-center justify-center h-[200px] text-muted-foreground">
                        No apartments with an Airbnb listing are configured.
                    </CardContent>
                </Card>
            );
        }
        if (filteredRows.length === 0) {
            return (
                <Card>
                    <CardContent className="flex items-center justify-center h-[200px] text-muted-foreground">
                        No apartments match the current filters.
                    </CardContent>
                </Card>
            );
        }
        return <ReviewPerformanceTable data={{ ...performance, rows: filteredRows }} loading={isPerformanceFetching} />;
    })();

    return (
        <div className="space-y-6" data-testid="reviews-graphs-page">
            {/* Header: shared apartment filters + Year / Period (wraps so the new
                selectors never push the page into a horizontal scrollbar). */}
            <div className="flex flex-col lg:flex-row lg:flex-wrap justify-between gap-2 space-y-2 lg:space-y-0 lg:space-x-2">
                <div className="flex flex-col gap-2">
                    <h1 className="text-2xl font-semibold">Airbnb Reviews Trends</h1>
                    <ApartmentFilterBar />
                </div>
                <div className="flex flex-col lg:flex-row justify-end gap-2 lg:space-x-2 lg:space-y-0 space-y-2">
                    <div data-testid="reviews-year-select">
                        <SearchableSelect
                            variant="volt"
                            onChange={(value) => setSelectedYear(Number(value))}
                            placeholder="Select Year"
                            options={yearOptions || []}
                            selectedValue={selectedYear.toString()}
                            width="w-auto"
                        />
                    </div>
                    <div data-testid="reviews-period-select">
                        <SearchableSelect
                            variant="volt"
                            onChange={(value) => setSelectedPeriod(value as PeriodName)}
                            placeholder="Select Period"
                            options={periodOptions || []}
                            selectedValue={selectedPeriod}
                            width="w-auto"
                        />
                    </div>
                </div>
            </div>

            {isPerformanceError ? (
                <Card>
                    <CardContent className="flex items-center justify-center h-[300px] text-destructive">
                        Something went wrong loading review performance. Please try again.
                    </CardContent>
                </Card>
            ) : (
                <Tabs defaultValue="aggregated" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="aggregated">Aggregated Trends</TabsTrigger>
                        <TabsTrigger value="apartments">Apartment Comparison</TabsTrigger>
                        <TabsTrigger value="groups">Group Trends</TabsTrigger>
                    </TabsList>

                    {/* Aggregated Trends Tab */}
                    <TabsContent value="aggregated" className="space-y-4">
                        {/* Metrics only. Apartment scope lives in the shared
                            filter bar above — a second, page-local apartment /
                            group picker here was both redundant and the source
                            of the "All is forgotten on reload" bug. */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Metrics</CardTitle>
                                <CardDescription>Choose which review scores to plot</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="min-w-[12rem] max-w-sm">
                                    <MultiSelect
                                        variant="volt"
                                        staticText="Selected Metrics"
                                        options={METRIC_OPTIONS}
                                        width="w-full"
                                        onChange={setSelectedMetrics}
                                        selectedValues={selectedMetrics}
                                        placeholder="Select Metrics"
                                    />
                                </div>
                                <div className="mt-4 text-sm text-muted-foreground">
                                    Showing trends for {filteredApartmentIds.length} apartment{filteredApartmentIds.length !== 1 ? "s" : ""}
                                </div>
                            </CardContent>
                        </Card>

                        {selectedMetrics.length > 0 ? (
                            <ReviewTrendChart
                                data={aggregatedChartData}
                                title={chartTitle}
                                description="Average ratings over the trailing 12 months; the selected period is shaded"
                                lines={selectedMetricLines}
                                highlight={highlight}
                                height={500}
                            />
                        ) : (
                            <Card>
                                <CardContent className="flex items-center justify-center h-[400px] text-muted-foreground">
                                    Please select at least one metric to display
                                </CardContent>
                            </Card>
                        )}

                        {performanceSection}
                    </TabsContent>

                    {/* Apartment Comparison Tab */}
                    <TabsContent value="apartments" className="space-y-4">
                        {/* The apartments compared here are the filter bar's
                            selection — the tab used to carry its own picker,
                            which meant two apartment dropdowns on one page. */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Metrics</CardTitle>
                                <CardDescription>
                                    Comparing the apartments selected in the filter bar above; choose which scores to plot
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="min-w-[12rem] max-w-sm">
                                    <MultiSelect
                                        variant="volt"
                                        staticText="Selected Metrics"
                                        options={METRIC_OPTIONS}
                                        width="w-full"
                                        onChange={setSelectedMetrics}
                                        selectedValues={selectedMetrics}
                                        placeholder="Select Metrics"
                                    />
                                </div>
                                {comparisonIsReadable && (
                                    <div className="mt-4 text-sm text-muted-foreground">
                                        Comparing {filteredApartmentIds.length} apartment
                                        {filteredApartmentIds.length !== 1 ? "s" : ""}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {comparisonIsReadable && apartmentComparisonData.length > 0 ? (
                            <ReviewTrendChart
                                data={apartmentComparisonChartData}
                                title={chartTitle}
                                description="Compare average ratings across the filtered apartments over the trailing 12 months"
                                lines={apartmentComparisonLines}
                                highlight={highlight}
                                height={500}
                            />
                        ) : (
                            <Card>
                                {/* Announced: the message changes as a side
                                    effect of the shared filter bar elsewhere on
                                    the page, and one variant asks the user to
                                    act (narrow the filters). Silent otherwise. */}
                                <CardContent
                                    role="status"
                                    aria-live="polite"
                                    className="flex items-center justify-center h-[400px] text-muted-foreground"
                                >
                                    {filteredApartmentIds.length === 0
                                        ? "No apartments match the current filters"
                                        : filteredApartmentIds.length > MAX_COMPARISON_APARTMENTS
                                          ? `${filteredApartmentIds.length} apartments match the current filters — narrow them in the filter bar above to compare up to ${MAX_COMPARISON_APARTMENTS} at once`
                                          : "No review data available for the filtered apartments in this date range"}
                                </CardContent>
                            </Card>
                        )}

                        {performanceSection}
                    </TabsContent>

                    {/* Group Trends Tab */}
                    <TabsContent value="groups" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Select Group</CardTitle>
                                <CardDescription>View review trends for a specific apartment group</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <SearchableSelect
                                    variant="volt"
                                    selectedValue={selectedGroup.toString()}
                                    onChange={(value) => setSelectedGroup(Number(value))}
                                    placeholder="Select Group"
                                    options={groupOptions}
                                    selectedValuePrefix="Group"
                                    className="w-72"
                                />
                            </CardContent>
                        </Card>

                        {selectedGroup !== 0 && groupTrendData.length > 0 && selectedMetrics.length > 0 ? (
                            <ReviewTrendChart
                                data={groupTrendChartData}
                                title={chartTitle}
                                description="Average ratings for all apartments in this group over the trailing 12 months"
                                lines={selectedMetricLines}
                                highlight={highlight}
                                height={500}
                            />
                        ) : selectedGroup !== 0 && groupTrendData.length > 0 && selectedMetrics.length === 0 ? (
                            <Card>
                                <CardContent className="flex items-center justify-center h-[400px] text-muted-foreground">
                                    Please select at least one metric to display
                                </CardContent>
                            </Card>
                        ) : (
                            <Card>
                                <CardContent className="flex items-center justify-center h-[400px] text-muted-foreground">
                                    {selectedGroup === 0
                                        ? "Select a group to view trends"
                                        : "No review data available for this group in the selected date range"}
                                </CardContent>
                            </Card>
                        )}

                        {performanceSection}
                    </TabsContent>
                </Tabs>
            )}
        </div>
    );
};

export default ReviewsGraphsPage;
